import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import get from 'lodash/get';
import { ButtonWithIcon } from '@els/els-react--button';
import { ORIENTATION_RIGHT_TO_LEFT } from '@Components/D3Tree/constants';
import RsTree from '@Components/D3Tree/Tree/RsTree';
import { renderCustomNodeElement } from './RouteThumbnailsTreeUtils';

import './RouteThumbnailsTree.scss';

/* eslint-disable react-hooks/exhaustive-deps */
function RouteThumbnailsTree({
  tree,
  list,
  routeName,
  isStereospecific = false,
  onTreeNodeClick = () => null,
  onZoomClick = () => null,
  allowTruncation = true,
  nodeWidth = 10,
  nodeHeight = 10,
  depthFactor = 20,
  rectSize = 16,
  circleSize = 5,
  focusableNodes = true,
}) {
  const treeContainerRef = useRef();
  const localContainerRef = useRef();
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [treeSize, setTreeSize] = useState();
  const [isTruncated, setIsTruncated] = useState();

  const truncatedTree = useMemo(() => {
    const children = [{ id: 'truncationNode', parentId: '0', isTruncationNode: true, substance: '', leafNode: false, children: [] }];
    return { ...tree, id: '0', children };
  }, [tree]);

  const resizeHandler = () => {
    try {
      const { width, height } = localContainerRef.current.getBoundingClientRect();
      const x = width - 2 * nodeWidth;
      const y = height / 2;
      if (treeSize && allowTruncation) {
        setIsTruncated(x - nodeWidth / 2 < get(treeSize, 'width') || height - 2 * nodeHeight < get(treeSize, 'height'));
      }
      setTranslate({ x, y });
    } catch (e) {
      Logger.error('RouteThumbnailsTree - container size error:', e);
    }
  };

  const onResizeLayout = ({ treeBoundaries }) => {
    const width = treeBoundaries.xMax - treeBoundaries.xMin;
    const height = treeBoundaries.yMax - treeBoundaries.yMin;
    setTreeSize({ width, height });
  };

  useLayoutEffect(() => {
    window.addEventListener('resize', resizeHandler);

    // Todos check ways of element size detection
    setTimeout(resizeHandler, 500);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [treeSize, isTruncated, tree]);

  const renderNodeHandler = ({ nodeDatum = {}, ...rest }) =>
    renderCustomNodeElement({ nodeDatum, ...rest }, { rectSize, circleSize, focusableNodes });

  return (
    <div className="route-thumbnail-tree--container" ref={localContainerRef}>
      {isStereospecific && <div className="route-thumbnail-tree--container__stereospecific">Stereospecific</div>}
      {list && (
        <ButtonWithIcon
          data-e2e="route-thumbnail-preview-button"
          className="route-thumbnail-tree--container__btns e2e-rs-route-table-zoom-in"
          sprite="ZoomIn"
          iconRight
          type="link"
          onClick={() => onZoomClick(list, routeName)}>
          Preview
        </ButtonWithIcon>
      )}
      <RsTree
        ref={treeContainerRef}
        onNodeClick={(id, event, data) => onTreeNodeClick(event.target, data)}
        nodeSize={{ x: nodeWidth, y: nodeHeight }}
        data={isTruncated ? truncatedTree : tree}
        separation={{
          siblings: 2,
          nonSiblings: 2,
        }}
        zoom={1}
        pathClassFunc={() => 'tree-wire'}
        zoomable={false}
        translate={translate}
        depthFactor={depthFactor}
        orientation={ORIENTATION_RIGHT_TO_LEFT}
        rootNodeClassName="node__root"
        branchNodeClassName="node__branch"
        leafNodeClassName="node__leaf"
        rootNodeAriaLabel="target"
        branchNodeAriaLabel="intermediate"
        leafNodeAriaLabel="starting material"
        enableLegacyTransitions={false}
        collapsible={false}
        renderCustomNodeElement={renderNodeHandler}
        onResizeLayout={onResizeLayout}
      />
    </div>
  );
}

RouteThumbnailsTree.propTypes = {
  routeName: PropTypes.string,
  tree: PropTypes.any,
  list: PropTypes.any,
  isStereospecific: PropTypes.bool,
  onTreeNodeClick: PropTypes.func,
  onZoomClick: PropTypes.func,
  allowTruncation: PropTypes.bool,
  focusableNodes: PropTypes.bool,
  nodeWidth: PropTypes.number,
  nodeHeight: PropTypes.number,
  depthFactor: PropTypes.number,
  rectSize: PropTypes.number,
  circleSize: PropTypes.number,
};

export default RouteThumbnailsTree;
